import { ReactNode, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchMenuItemSettings, shownMenuKeys, GATED_MENU_KEYS, MENU_SETTINGS_CHANGED_EVENT,
} from '../../services/menuItemSettingsService';
import {
  LayoutDashboard, Plane, FileText, Settings, LogOut, Menu, X, Award,
  Database, Shield, Upload, DollarSign, Briefcase, Brain, LifeBuoy,
  Sparkles, Globe, Search as SearchIcon, Users, RefreshCw, Terminal,
  TrendingUp, Zap, Activity, AlertTriangle, Clock, History,
  Grid, Building, User, MessageSquare, Building2, Inbox, Wallet,
  CheckSquare, Mail, Landmark, FileCheck, TrendingDown, UserPlus,
  ChevronsLeft, CreditCard, Link, Hotel,
  Car, BarChart3, PieChart, Bell, Receipt, Scale, MapPin, Layers,
  ServerCog, ClipboardList, Calculator, Book, Monitor, FolderOpen,
  Banknote, Target, UsersRound, ListChecks, FileSpreadsheet, Tags,
  Calendar, ArrowDownUp, Percent, CircleDollarSign, Filter, Bot, Radio, Star,
  Compass, Scissors, Phone, Plug, Reply, Hourglass, CalendarClock,
} from 'lucide-react';
import PropellerLogo from '../../assets/Propeller Travel Logo 448x100 copy.png';
import { QuickActionsMenu } from '../Common/QuickActionsMenu';
import { ViewAsCustomerPalette, useViewAsCustomerHotkey } from '../Admin/ViewAsCustomerPalette';
import { Breadcrumbs } from '../Common/Breadcrumbs';
import { OperatorDesignKey } from '../OperatorDesign/OperatorDesignKey';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { VersionDisplay } from '../Common/VersionDisplay';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useNavigationStore } from '../../stores/navigationStore';
import { TabbedNavigation } from './TabbedNavigation';
import { NavigationFavorites } from './NavigationFavorites';
import { NavigationRecents } from './NavigationRecents';
import { NavigationSearch } from './NavigationSearch';
import { NavigationSection } from './NavigationSection';
import { QuickActionsBar } from './QuickActionsBar';
import { NavigationMostUsed } from './NavigationMostUsed';
import { NavigationShortcuts } from './NavigationShortcuts';
import { NavigationOnboarding } from './NavigationOnboarding';
import { NavigationSuggestedNext } from './NavigationSuggestedNext';
import { NavigationHelp } from './NavigationHelp';
import { NavigationSettings } from './NavigationSettings';
import { MobileNavigationModalBoundary } from './MobileNavigationModalBoundary';
import { PageHelpButton } from '../Common/PageHelpButton';
import { SkipToContentLink, MAIN_CONTENT_ID } from './SkipToContentLink';
import { useOverlayOpen } from '../../hooks/useOverlayOpen';
import { MasterSearchPalette } from '../ui/MasterSearchPalette';
import { PNT_LONG } from '../../constants/pnt';
import '../../styles/navigation.css';
import { StagingBanner } from './StagingBanner';
import { PresentationModeToggle } from '../Common/PresentationModeToggle';
import { usePresentationStore } from '../../stores/presentationStore';
import { useQueueBadgeCount } from '../../hooks/useQueueBadgeCount';
import { useBankFeedHealth, bankFeedNavBadge } from '../../hooks/useBankFeedHealth';
import { NotificationBell, NotificationPanel } from '../Notifications';
import { DkManagementAssistantDrawer } from '../ops/DkManagementAssistantDrawer';
import { WhatsAppSystemStatusBar } from '../Operations/WhatsAppSystemStatusBar';
import { usePrivacyModePersistence } from '../../hooks/usePrivacyMode';
import { findRouteSectionId, resolveRouteTab } from '../../utils/navigationRouteSection';
import {
  ADDITIONAL_SEARCH_PERMISSION_PATHS,
  getAllowedShortcutTarget,
  getNavigationAuthorizationPath,
  getNavigationPermissionPath,
} from '../../utils/navigationSearch';
import {
  canCommitNavigationUserLookup,
  getEffectiveAllowedPaths,
  isPermissionEvaluationPending,
  type NavigationPermissionState,
} from '../../utils/navigationPermissionState';
import {
  EMPTY_NAVIGATION_ROLE_STATE,
  getVisibleNavigationRoleState,
} from '../../utils/navigationRoleState';
import { OPS_SURFACE_LABELS } from '../../config/opsSurfaceLabels';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DOMAIN_LANDING_PATHS: Record<'operations' | 'crm' | 'finance' | 'intelligence' | 'admin', string> = {
  operations: '/ops/inbox',
  crm: '/crm',
  finance: '/finance/payment-economics',
  intelligence: '/reports',
  admin: '/admin/system-health',
};

function formatRoleLabel(role: string): string {
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface NavItem {
  id: string;
  name: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  isNew?: boolean;
  /**
   * A feature that is built but has no live data source, shown greyed with a reason.
   *
   * Still navigable on purpose. The pages hold real historical records and the surface
   * is not broken — what is missing is the feed. Hiding it entirely would make a
   * built capability look absent; leaving it undifferentiated would let stale figures
   * read as current. Greying it and saying why is the honest middle.
   */
  unavailable?: string;
  /** Short pill text for the greyed entry. Defaults to 'No feed'. */
  unavailableLabel?: string;
}

interface NavSection {
  id: string;
  name: string;
  icon: React.ElementType;
  items: NavItem[];
  tab: 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';
  description?: string;
}

/**
 * The nav entries whose data comes from the bank feed.
 *
 * Listed here rather than flagged in the section definitions because whether these are
 * usable is a runtime fact about the viewing user's connections, not a property of the
 * route. Hardcoding it made the badge unclearable and told users with a working Revolut,
 * Brex or Mercury connection that they had no feed.
 */
const BANK_FEED_NAV_IDS = new Set([
  'banking',
  'banking-categorize',
  'banking-rules',
  'banking-analytics',
  'banking-categories',
  'banking-resolutions',
]);

export function DashboardLayout({ children }: DashboardLayoutProps) {
  // Shared route boundary for the privacy/overhead mode: persist the URL state
  // here so it survives navigation from ANY authenticated surface — including
  // ones that derive privacy locally and never call usePrivacyMode (e.g. the
  // AOC control tower) — and the destination masks PII fail-safe.
  usePrivacyModePersistence();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigationRoleState, setNavigationRoleState] = useState(EMPTY_NAVIGATION_ROLE_STATE);
  const [searchOpen, setSearchOpen] = useState(false);
  // PT-AUDIT-0147/0149/0171: floating launchers step aside while any dialog is open.
  const overlayOpen = useOverlayOpen();
  /**
   * Global "View as customer…" palette state. Opens on Cmd/Ctrl+Shift+K
   * (avoiding clash with MasterSearchPalette's Cmd/Ctrl+K). Gated on
   * staff roles below — `admin` / `super_admin` / `manager` only.
   */
  const [viewAsOpen, setViewAsOpen] = useViewAsCustomerHotkey();

  const currentUserId = user?.id ?? null;
  const visibleNavigationRoleState = getVisibleNavigationRoleState(
    navigationRoleState,
    currentUserId,
  );
  const {
    isAdmin,
    isSuperAdmin: isSuperAdminUser,
    userRole,
  } = visibleNavigationRoleState;
  const metadataRole = typeof user?.user_metadata?.role === 'string'
    ? user.user_metadata.role
    : undefined;
  const [permissionState, setPermissionState] = useState<NavigationPermissionState>({
    userId: null,
    status: 'pending',
    paths: new Set(),
  });
  const allowedPaths = useMemo(
    () => getEffectiveAllowedPaths(permissionState, currentUserId),
    [permissionState, currentUserId],
  );
  const permissionsLoading = isPermissionEvaluationPending(permissionState, currentUserId);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const desktopExpandButtonRef = useRef<HTMLButtonElement>(null);
  const desktopCollapseButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarNavRef = useRef<HTMLElement>(null);
  const mainContentRef = useRef<HTMLElement>(null);
  // CRM pages hold dense tables; they keep a right gutter for the floating help and quick-action buttons.
  const keepsFloatingButtonGutter = location.pathname === '/crm' || location.pathname.startsWith('/crm/');
  const operatorDesignRoute = ['/ops/today', '/ops/waiting', '/ucc', '/rtw/desk-queue', '/finance', '/hotels', '/admin/delivery', '/ops/inbox'].includes(location.pathname) || location.pathname.startsWith('/ucc/') || /^\/crm\/customers\/[^/]+\/?$/.test(location.pathname);
  const sidebarOpenRef = useRef(false);
  const previousLocationPathRef = useRef(location.pathname);
  const currentUserIdRef = useRef(currentUserId);
  const roleLookupGenerationRef = useRef(0);
  currentUserIdRef.current = currentUserId;

  const {
    activeTab,
    setActiveTab,
    density: storedDensity,
    navigationPreferencesOwnerId,
    navigationPreferencesStatus,
    bindNavigationPreferencesOwner,
    addRecentPage,
    setRecentPagesOwner,
    filterRecentPages,
    expandSection,
    collapseAll,
    expandAll,
    loadFromDatabase,
    isSidebarCollapsed,
    toggleSidebar
  } = useNavigationStore();

  const navigationPreferencesBound = Boolean(
    currentUserId && navigationPreferencesOwnerId === currentUserId,
  );
  const density = navigationPreferencesBound ? storedDensity : 'normal';

  useLayoutEffect(() => {
    bindNavigationPreferencesOwner(currentUserId);
    setRecentPagesOwner(currentUserId);
  }, [bindNavigationPreferencesOwner, currentUserId, setRecentPagesOwner]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktopViewport(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const lookupGeneration = ++roleLookupGenerationRef.current;
    const expectedUserId = currentUserId;
    const controller = new AbortController();
    const canCommit = () => canCommitNavigationUserLookup(
      expectedUserId,
      currentUserIdRef.current,
      lookupGeneration,
      roleLookupGenerationRef.current,
      controller.signal,
    );

    setNavigationRoleState({
      ...EMPTY_NAVIGATION_ROLE_STATE,
      ownerId: expectedUserId,
    });

    if (!expectedUserId) {
      return () => controller.abort();
    }

    const checkAdminStatus = async () => {
      try {
        const { isSuperAdmin } = await import('../../utils/adminAuth');
        const adminStatus = await isSuperAdmin();
        if (!canCommit()) return;
        setNavigationRoleState((state) => ({
          ...state,
          ownerId: expectedUserId,
          isAdmin: adminStatus || state.isAdmin,
          isSuperAdmin: adminStatus || state.isSuperAdmin,
        }));
      } catch (error) {
        if (!canCommit()) return;
        console.error('Error checking admin status:', error);
        setNavigationRoleState((state) => ({
          ...state,
          ownerId: expectedUserId,
          isAdmin: state.isAdmin || metadataRole === 'super_admin' || metadataRole === 'admin',
          isSuperAdmin: state.isSuperAdmin || metadataRole === 'super_admin',
        }));
      }
    };

    void checkAdminStatus();

    // Load navigation preferences from database for the current account.
    void loadFromDatabase(expectedUserId, controller.signal);

    void import('../../lib/supabase').then(async ({ supabase }) => {
      const { data, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', expectedUserId)
          .abortSignal(controller.signal)
          .maybeSingle();
      if (!canCommit()) return;
      if (error) {
        console.error('Error fetching user role:', error);
        if (metadataRole) {
          setNavigationRoleState({
            ownerId: expectedUserId,
            userRole: metadataRole,
            isAdmin: metadataRole === 'super_admin' || metadataRole === 'admin',
            isSuperAdmin: metadataRole === 'super_admin',
          });
        }
        return;
      }
      if (data?.role) {
        setNavigationRoleState({
          ownerId: expectedUserId,
          userRole: data.role,
          isAdmin: data.role === 'super_admin' || data.role === 'admin',
          isSuperAdmin: data.role === 'super_admin',
        });
      }
    }).catch((error) => {
      if (!canCommit()) return;
      console.error('Error fetching user role:', error);
    });

    return () => controller.abort();
  }, [currentUserId, metadataRole, loadFromDatabase]);


  // Restructured Navigation - 5 Domains
  const { isPresentationMode } = usePresentationStore();
  const queueBadge = useQueueBadgeCount();

  const baseNavigationSections: NavSection[] = useMemo(() => [
    {
      id: 'operations-command',
      name: 'Operations Command',
      icon: Radio,
      tab: 'operations',
      description: 'Situation awareness, manager dispatch, and operational trust',
      items: [
        { id: 'ops-today', name: OPS_SURFACE_LABELS.today.label, icon: ListChecks, path: OPS_SURFACE_LABELS.today.href, isNew: true },
        { id: 'ops-inbox', name: 'Ops Inbox', icon: Inbox, path: '/ops/inbox', isNew: true },
        { id: 'ops-reply-queue', name: OPS_SURFACE_LABELS['reply-review'].label, icon: Reply, path: OPS_SURFACE_LABELS['reply-review'].href, isNew: true },
        { id: 'ops-waiting', name: 'Waiting for a reply', icon: Hourglass, path: '/ops/waiting', isNew: true },
        { id: 'ops-lift-shift', name: 'Lift and shift', icon: CalendarClock, path: '/ops/lift-and-shift', isNew: true },
        { id: 'ops-calls', name: 'Calls', icon: Phone, path: '/calls', isNew: true },
        { id: 'ops-customer-requests', name: OPS_SURFACE_LABELS['customer-requests'].label, icon: MessageSquare, path: OPS_SURFACE_LABELS['customer-requests'].href, isNew: true },
        { id: 'ops-supervisor', name: OPS_SURFACE_LABELS['team-dispatch'].label, icon: ListChecks, path: OPS_SURFACE_LABELS['team-dispatch'].href, isNew: true },
        { id: 'ops-control-tower', name: 'Ops Control Tower', icon: Monitor, path: '/ops/control-tower', isNew: true },
        { id: 'aoc', name: 'Aviation Ops Center', icon: Radio, path: '/aoc', isNew: true },
        { id: 'ops-handbook', name: 'AOC + Ops Handbook', icon: Book, path: '/ops/handbook', isNew: true },
      ],
    },
    {
      id: 'quick-dashboard',
      name: 'Dashboard',
      icon: LayoutDashboard,
      tab: 'operations',
      description: 'Main dashboard and overview',
      items: [
        { id: 'dashboard', name: 'Home', icon: LayoutDashboard, path: '/' },
        { id: 'customer-location', name: 'Customer Locations', icon: Globe, path: '/dashboard/locations' },
      ]
    },
    {
      id: 'bookings-hub',
      name: 'Bookings',
      icon: SearchIcon,
      tab: 'operations',
      description: 'Search, book, and manage reservations',
      items: [
        { id: 'unified-flight-search', name: 'Flight Search', icon: SearchIcon, path: '/search' },
        { id: 'advanced-search', name: 'Advanced Search', icon: SearchIcon, path: '/search/advanced' },
        { id: 'consolidator-search', name: 'Agentic Fare Search', icon: Zap, path: '/consolidators' },
        { id: 'paste-and-book', name: 'Quick Book', icon: Zap, path: '/quick-book' },
        { id: 'air-itinerary-composer', name: 'Client Air Itinerary', icon: Plane, path: '/air/itinerary-composer', isNew: true },
        { id: 'price-comparison', name: 'Price Compare', icon: TrendingUp, path: '/price-comparison' },
        { id: 'enhanced-bookings', name: 'All Bookings', icon: Grid, path: '/bookings' },
        { id: 'ticketing-deadlines', name: 'Ticketing deadlines', icon: Clock, path: '/bookings/deadlines', isNew: true },
        { id: 'pnr', name: 'Booking Manager', icon: FileText, path: '/pnr' },
        { id: 'pnt', name: isPresentationMode ? 'Upcoming Ticketing List' : PNT_LONG, icon: Briefcase, path: '/pnt' },
        { id: 'group-bookings', name: 'Group Bookings', icon: UsersRound, path: '/groups', isNew: true },
      ]
    },
    {
      id: 'trip-management',
      name: 'Trip Management',
      icon: MapPin,
      tab: 'operations',
      description: 'Manage trips, itineraries, and customer journeys',
      items: [
        { id: 'trips', name: 'All Trips', icon: MapPin, path: '/trips', isNew: true },
        { id: 'trip-files', name: 'Trip Files', icon: FolderOpen, path: '/trip-files' },
        { id: 'advanced-itinerary', name: 'Full Proposal Builder', icon: FileText, path: '/proposals' },
        { id: 'itinerary-builder', name: 'Quick Proposal Builder', icon: Plane, path: '/itinerary/builder' },
        { id: 'proposal-tracking', name: 'Proposal Tracking', icon: TrendingUp, path: '/proposals/tracking' },
      ]
    },
    {
      id: 'hotels-cars',
      name: 'Hotels & Cars',
      icon: Hotel,
      tab: 'operations',
      description: 'Hotel and car rental bookings',
      items: [
        { id: 'hotel-search', name: 'Hotel Search', icon: Hotel, path: '/hotels' },
        { id: 'hotel-bookings', name: 'Hotel Bookings', icon: ClipboardList, path: '/hotels/bookings', isNew: true },
        { id: 'ratehawk-search', name: 'RateHawk Hotels', icon: Hotel, path: '/hotels/ratehawk' },
        { id: 'ratehawk-bookings', name: 'RateHawk Bookings', icon: ClipboardList, path: '/hotels/ratehawk/bookings' },
        { id: 'ratehawk-payments', name: 'RateHawk Payments', icon: CreditCard, path: '/hotels/ratehawk/payments' },
        { id: 'car-search', name: 'Car Rental', icon: Car, path: '/cars' },
      ]
    },
    {
      id: 'gds-tools',
      name: 'GDS Tools',
      icon: Database,
      tab: 'operations',
      description: 'Direct GDS access and tools',
      items: [
        { id: 'sabre-dashboard', name: 'Sabre Dashboard', icon: Database, path: '/sabre' },
        { id: 'sabre-availability', name: 'Sabre Availability', icon: SearchIcon, path: '/sabre/availability' },
        { id: 'sabre-pnr', name: 'Sabre PNR Create', icon: FileText, path: '/sabre/pnr-create' },
        { id: 'sabre-fare-rules', name: 'Fare Rules', icon: Book, path: '/sabre/fare-rules' },
        { id: 'sabre-ancillary', name: 'Ancillary Services', icon: Layers, path: '/sabre/ancillary' },
        { id: 'sabre-destination-finder', name: 'Destination Finder', icon: Compass, path: '/sabre/destination-finder', isNew: true },
        { id: 'sabre-pnr-split', name: 'PNR Split', icon: Scissors, path: '/sabre/pnr-split', isNew: true },
        { id: 'sabre-cryptic-command', name: 'Sabre Terminal', icon: Terminal, path: '/sabre/terminal' },
        { id: 'amadeus-terminal', name: 'Amadeus Terminal', icon: Terminal, path: '/amadeus/terminal' },
        { id: 'duffel-ndc', name: 'Duffel NDC', icon: Plane, path: '/duffel' },
      ]
    },
    {
      id: 'queues-automation',
      name: 'Queues & Automation',
      icon: Activity,
      tab: 'operations',
      description: 'Monitor queues and manage automation rules',
      items: [
        // The live queue count is added in navigationSections below, so a count change never re-runs the route
        // permission checks (which emptied the menu while they ran).
        { id: 'queue-center', name: 'Queue Command Center', icon: Activity, path: '/queue-center' },
        { id: 'queue-actions', name: 'Queue Actions', icon: Inbox, path: '/automation/queue-actions' },
        { id: 'ttl-monitor', name: 'Time Limits Monitor', icon: Clock, path: '/automation/ttl' },
        { id: 'auto-checkin', name: 'Auto Check-In', icon: Plane, path: '/automation/checkin' },
        { id: 'queue-manual', name: 'Operations Manual (PDF)', icon: Book, path: '/manuals?m=queue-operations', isNew: true },
      ]
    },
    {
      id: 'customer-hub',
      name: 'Customer Hub',
      icon: Users,
      tab: 'crm',
      description: 'Manage customers and relationships',
      items: [
        { id: 'customer-directory', name: 'Customers', icon: Users, path: '/crm/customers' },
        { id: 'customers', name: 'CRM Home', icon: Users, path: '/crm' },
        { id: 'customer-segments', name: 'Customer Segments', icon: Target, path: '/crm/segments', isNew: true },
        { id: 'companies', name: 'Companies', icon: Building, path: '/companies' },
        { id: 'travelers', name: 'Traveler Profiles', icon: User, path: '/travelers' },
        { id: 'leads', name: 'Leads', icon: UserPlus, path: '/leads' },
        { id: 'crm-suppliers', name: 'Suppliers (360 Directory)', icon: Building2, path: '/crm/suppliers' },
        { id: 'crm-providers', name: 'Suppliers & Providers', icon: Building2, path: '/crm/providers', isNew: true },
        { id: 'crm-hotels', name: 'Hotel Properties', icon: Hotel, path: '/crm/hotels', isNew: true },
        { id: 'supplier-rates', name: 'Supplier Rates', icon: Percent, path: '/suppliers/rates', isNew: true },
        { id: 'unified-comms', name: 'UCC', icon: MessageSquare, path: '/ucc' },
        { id: 'communication-logs', name: 'Communication Logs', icon: MessageSquare, path: '/communication-logs' },
        { id: 'crm-suggested-updates', name: 'Suggested updates', icon: Sparkles, path: '/crm/suggested-updates', isNew: true },
        { id: 'crm-contact-matching', name: 'Customer matching', icon: Sparkles, path: '/crm/contact-matching', isNew: true },
        { id: 'crm-whatsapp-notes', name: 'WhatsApp notes in Freshdesk', icon: MessageSquare, path: '/crm/whatsapp-notes', isNew: true },
        { id: 'documents', name: 'Document Vault', icon: Shield, path: '/documents' },
        { id: 'document-manager', name: 'Document Manager', icon: FileText, path: '/documents/manager' },
        { id: 'doc-acknowledgements', name: 'Doc Acknowledgements', icon: FileCheck, path: '/crm/documents/acknowledgements', isNew: true },
        { id: 'vip-support', name: 'VIP Support Queue', icon: Star, path: '/vip-support' },
        { id: 'pax-portal-preview', name: 'Pax Portal (preview)', icon: Compass, path: '/propeller', isNew: true },
      ]
    },

    {
      id: 'loyalty-planner',
      name: 'Loyalty & Planner',
      icon: Plane,
      tab: 'operations',
      description: 'Loyalty programs, calculators, and planners',
      items: [
        { id: 'rtw', name: 'RTW Planner', icon: Plane, path: '/rtw' },
        { id: 'rtw-shared-links', name: 'RTW Shared Links', icon: Link, path: '/rtw/shared-links' },
        { id: 'rtw-invite-drafts', name: 'RTW Invite Drafts', icon: Mail, path: '/rtw/invite-drafts' },
        { id: 'loyalty', name: 'Programme Rule Links', icon: Award, path: '/loyalty' },
        { id: 'tier-points-calculator', name: '2025 Itinerary Estimate', icon: Calculator, path: '/tier-points-calculator' },
      ]
    },


    // ========== INTELLIGENCE TAB ==========
    {
      id: 'ai-tools',
      name: 'AI Tools',
      icon: Sparkles,
      tab: 'intelligence',
      description: 'AI-powered assistance and automation',
      items: [
        { id: 'ai-assistant', name: 'AI Assistant', icon: Sparkles, path: '/ai-assistant' },
        { id: 'sabre-agent', name: 'Sabre AI Agent', icon: Bot, path: '/ai/sabre-agent', isNew: true },
        { id: 'amadeus-agent', name: 'Amadeus AI Agent', icon: Bot, path: '/ai/amadeus-agent', isNew: true },
        { id: 'ai-threads', name: 'AI Threads', icon: MessageSquare, path: '/ai/threads' },
        { id: 'pnr-agent', name: 'AI PNR Agent', icon: Sparkles, path: '/ai/pnr-agent' },
        { id: 'pnr-parser', name: 'PNR Parser', icon: FileText, path: '/ai/pnr-parser' },
        { id: 'knowledge-base', name: 'Knowledge Base', icon: Book, path: '/knowledge-base', isNew: true },
        { id: 'ai-usage-stats', name: 'AI Usage Stats', icon: Brain, path: '/ai/stats' },
      ]
    },
    {
      id: 'analytics-reports',
      name: 'Analytics & Reports',
      icon: TrendingUp,
      tab: 'intelligence',
      description: 'Business intelligence and insights',
      items: [
        { id: 'reports', name: 'Reporting Center', icon: BarChart3, path: '/reports', isNew: true },
        { id: 'reports-business', name: 'Business Reports', icon: PieChart, path: '/reports/business' },
        { id: 'reports-operations', name: 'Operations', icon: Activity, path: '/reports/operations' },
        { id: 'reports-ingestion', name: 'Ingestion Pipelines', icon: Mail, path: '/reports/ingestion' },
        { id: 'reports-cron', name: 'Cron Health', icon: Clock, path: '/reports/cron' },
        { id: 'reports-events', name: 'Event Stream', icon: Radio, path: '/reports/events' },
        { id: 'reports-deployment', name: 'Deployment Status', icon: Shield, path: '/reports/deployment' },
        { id: 'reports-pax-activity', name: 'Pax Portal Activity', icon: User, path: '/reports/pax_activity', isNew: true },
        { id: 'revenue-dashboard', name: 'Revenue & Margin', icon: DollarSign, path: '/revenue' },
        { id: 'revenue-analytics', name: 'Revenue Analytics', icon: CircleDollarSign, path: '/revenue/analytics', isNew: true },
        { id: 'agent-performance', name: 'Agent Performance', icon: Users, path: '/analytics/agents' },
        { id: 'agent-performance-dashboard', name: 'Agent Metrics & Targets', icon: Target, path: '/analytics/agent-performance', isNew: true },
        { id: 'queue-analytics', name: 'Queue Reports (Operations)', icon: Activity, path: '/analytics/queue' },
        { id: 'pnt-analytics', name: 'PNT Analytics', icon: TrendingUp, path: '/pnt/analytics' },
      ]
    },
    {
      id: 'operations-tools',
      name: 'Operational Monitoring',
      icon: RefreshCw,
      tab: 'intelligence',
      description: 'Operational utilities and monitors',
      items: [
        { id: 'pnr-history', name: 'PNR History', icon: History, path: '/pnr/history' },
        { id: 'interface-import', name: 'Interface Import', icon: Upload, path: '/interface-import' },
        { id: 'interface-monitoring', name: 'File Monitor', icon: RefreshCw, path: '/interface-files' },
        { id: 'document-generator', name: 'Document Generator', icon: FileText, path: '/documents/generate' },
        { id: 'templates', name: 'Templates', icon: FileText, path: '/templates' },
        { id: 'document-templates', name: 'Document Templates', icon: FileSpreadsheet, path: '/templates/documents', isNew: true },
        { id: 'email-templates-ops', name: 'Email Templates', icon: Mail, path: '/templates/emails', isNew: true },
        { id: 'data-quality', name: 'Data Quality', icon: AlertTriangle, path: '/data-quality' },
      ]
    },
    {
      id: 'risk-alerts',
      name: 'Risk & Alerts',
      icon: AlertTriangle,
      tab: 'intelligence',
      description: 'Travel risk and flight alerts',
      items: [
        { id: 'travel-risk', name: 'Travel Risk Dashboard', icon: MapPin, path: '/risk' },
        { id: 'flight-alerts', name: 'Flight Alerts', icon: Bell, path: '/alerts/flights' },
        { id: 'smart-alerts', name: 'Smart Alerts', icon: Zap, path: '/smart-alerts', isNew: true },
      ]
    },
    {
      id: 'support-center',
      name: 'Support Center',
      icon: LifeBuoy,
      tab: 'intelligence',
      description: 'Help desk and tickets',
      items: [
        { id: 'freshdesk', name: 'Freshdesk Dashboard', icon: LifeBuoy, path: '/freshdesk' },
        { id: 'freshdesk-sync', name: 'Sync Status', icon: RefreshCw, path: '/freshdesk/sync' },
      ]
    },

    // ========== FINANCE DOMAIN ==========
    {
      id: 'billing',
      name: 'Billing',
      icon: FileCheck,
      tab: 'finance',
      description: 'Invoices and payments',
      items: [
        { id: 'invoices', name: 'Invoices', icon: FileText, path: '/invoices' },
        { id: 'invoice-templates', name: 'Invoice Templates', icon: FileText, path: '/invoices/templates' },
        { id: 'payments', name: 'Payments', icon: DollarSign, path: '/payments' },
        { id: 'refunds', name: 'Refunds', icon: RefreshCw, path: '/finance/refunds' },
        { id: 'terminal', name: 'Virtual Terminal', icon: Landmark, path: '/finance/terminal' },
        { id: 'moto-terminal', name: 'Card Charging Terminal', icon: CreditCard, path: '/finance/moto' },
        { id: 'payment-methods', name: 'Payment Methods & Terms', icon: CreditCard, path: '/finance/payment-methods', isNew: true },
      ]
    },
    {
      id: 'accounting',
      name: 'Accounting',
      icon: DollarSign,
      tab: 'finance',
      description: 'Financial management and reporting',
      items: [
        { id: 'finance', name: 'Overview', icon: DollarSign, path: '/finance' },
        { id: 'ar-aging', name: 'AR Aging', icon: Clock, path: '/finance/ar-aging', isNew: true },
        { id: 'ap-management', name: 'AP Management', icon: Banknote, path: '/finance/ap-management', isNew: true },
        { id: 'cashflow', name: 'Cash Flow', icon: TrendingUp, path: '/finance/cashflow' },
        { id: 'pnt-aging', name: 'PNT Aging', icon: Clock, path: '/finance/aging' },
        { id: 'service-fees', name: 'Service Fees', icon: Tags, path: '/finance/service-fees', isNew: true },
        { id: 'payment-plans', name: 'Payment Plans', icon: Calendar, path: '/finance/payment-plans', isNew: true },
        { id: 'payment-sources', name: 'Payment Sources', icon: Link, path: '/finance/payment-sources', isNew: true },
        { id: 'stripe-balance', name: 'Stripe Balance Sync', icon: CreditCard, path: '/finance/stripe-balance', isNew: true },
        { id: 'stripe-settlement', name: 'Stripe Settlement', icon: Banknote, path: '/finance/stripe-settlement', isNew: true },
        { id: 'payment-economics', name: 'Payment Economics', icon: CircleDollarSign, path: '/finance/payment-economics', isNew: true },
        { id: 'stripe-customers', name: 'Stripe Customer Sync', icon: Users, path: '/finance/stripe-customers', isNew: true },
        { id: 'supplier-invoices', name: 'Supplier Invoices', icon: FileText, path: '/finance/supplier-invoices' },
        { id: 'statement-reconciliation', name: 'Statement Reconciliation', icon: FileCheck, path: '/finance/statement-reconciliation' },
        { id: 'supplier-credit', name: 'Supplier Credit', icon: CreditCard, path: '/finance/supplier-credit' },
        { id: 'supplier-config', name: 'Supplier Setup', icon: Settings, path: '/finance/supplier-config' },
        { id: 'fora-integration', name: 'Fora Commissions', icon: Hotel, path: '/finance/fora', isNew: true },
        { id: 'settlements', name: 'Settlements', icon: Wallet, path: '/finance/settlements' },
        { id: 'commissions', name: 'Commissions', icon: TrendingUp, path: '/commissions' },
        { id: 'commission-engine', name: 'Commission Engine', icon: Calculator, path: '/finance/commission-engine' },
        { id: 'banking', name: 'Bank Accounts', icon: Building2, path: '/banking' },
        { id: 'banking-categorize', name: 'Categorize Txns', icon: ArrowDownUp, path: '/banking/categorize', isNew: true },
        { id: 'banking-rules', name: 'Merchant Rules', icon: Filter, path: '/banking/rules', isNew: true },
        { id: 'banking-analytics', name: 'Banking Intelligence', icon: BarChart3, path: '/banking/analytics', isNew: true },
        { id: 'banking-categories', name: 'Categories', icon: Tags, path: '/banking/categories', isNew: true },
        { id: 'banking-resolutions', name: 'Resolutions', icon: ListChecks, path: '/banking/resolutions', isNew: true },
        { id: 'reconciliation', name: 'Reconciliation', icon: FileCheck, path: '/payments/reconciliation' },
        { id: 'chart-of-accounts', name: 'Chart of Accounts', icon: Book, path: '/finance/chart-of-accounts', isNew: true },
        { id: 'journal-entries', name: 'Journal Entries', icon: FileText, path: '/finance/journal-entries', isNew: true },
        { id: 'client-charges', name: 'Client Charges', icon: Receipt, path: '/finance/client-charges', isNew: true },
      ]
    },
    {
      id: 'partners',
      name: 'Partners',
      icon: UsersRound,
      tab: 'finance',
      description: 'Manage ICs, affiliates, and host agencies',
      items: [
        { id: 'partners-all', name: 'All Partners', icon: UsersRound, path: '/partners', isNew: true },
        { id: 'partners-add', name: 'Add Partner', icon: UserPlus, path: '/partners/new', isNew: true },
        { id: 'partners-payouts', name: 'Partner Payouts', icon: Banknote, path: '/partners/payouts', isNew: true },
      ]
    },
    {
      id: 'tasks-automation',
      name: 'Tasks & Automation',
      icon: CheckSquare,
      tab: 'operations',
      description: 'Tasks, actions, and automated routines',
      items: [
        { id: 'tasks', name: 'Task Dashboard', icon: CheckSquare, path: '/tasks', isNew: true },
        { id: 'task-management', name: 'Task Management', icon: ListChecks, path: '/tasks/management', isNew: true },
        { id: 'action-plans', name: 'Action Plans', icon: ClipboardList, path: '/action-plans', isNew: true },
        { id: 'routines', name: 'Routines', icon: RefreshCw, path: '/routines', isNew: true },
        { id: 'qc-checklists', name: 'QC Checklists', icon: ClipboardList, path: '/qc-checklists', isNew: true },
      ]
    },
    {
      id: 'expenses-compliance',
      name: 'Expenses & Compliance',
      icon: Receipt,
      tab: 'finance',
      description: 'Expense tracking and compliance',
      items: [
        { id: 'expenses', name: 'My Expenses', icon: Receipt, path: '/expenses' },
        { id: 'compliance-audit', name: 'Audit Trail', icon: Scale, path: '/compliance' },
      ]
    },
    // ========== ADMIN DOMAIN ==========
    {
      id: 'system-settings',
      name: 'System Settings',
      icon: Settings,
      tab: 'admin',
      description: 'Configuration and preferences',
      items: [
        { id: 'integrations', name: 'Integrations', icon: Link, path: '/integrations' },
        { id: 'gds', name: 'GDS Configuration', icon: Database, path: '/settings/gds' },
        { id: 'todoist', name: 'Todoist Integration', icon: CheckSquare, path: '/integrations/todoist' },
        { id: 'terms-templates', name: 'T&Cs Templates', icon: FileText, path: '/admin/terms-templates', isNew: true },
        { id: 'next-work-digest', name: 'Morning Slack Summary', icon: MessageSquare, path: '/settings/next-work-digest', isNew: true },
        { id: 'morning-message', name: 'Morning Message (each person)', icon: MessageSquare, path: '/settings/morning-message', isNew: true },
        { id: 'schedule-changes', name: 'Airline Schedule Changes', icon: Plane, path: '/settings/schedule-changes', isNew: true },
        { id: 'ai-personas-shifts', name: 'AI Personas & Shift Hours', icon: Users, path: '/settings/personas', isNew: true },
        { id: 'team-ai-manual', name: 'Team AI Playbook & Manual', icon: Book, path: '/ai-manual', isNew: true },
        { id: 'settings', name: 'Settings', icon: Settings, path: '/settings' },
        { id: 'ucc-settings', name: 'Unified Comms Settings', icon: MessageSquare, path: '/settings/communications' },
        { id: 'automation-rules', name: 'Automation Rules', icon: Zap, path: '/settings/automations' },
      ]
    },
    // ========== DEVELOPER DOMAIN ==========
    {
      id: 'developer-api',
      name: 'API & Integrations',
      icon: Terminal,
      tab: 'admin',
      description: 'Give Claude, ChatGPT, and other systems access to platform data',
      items: [
        { id: 'api-access', name: 'API Access Tokens', icon: Terminal, path: '/settings/api-access' },
        { id: 'api-docs', name: 'API Reference', icon: Book, path: '/api-docs' },
        { id: 'fora-sync', name: 'Fora Portal Sync', icon: Plug, path: '/automations/fora-sync', isNew: true },
        { id: 'portal-profiles', name: 'Portal Browser Profiles', icon: ServerCog, path: '/admin/portal-profiles', isNew: true },
      ]
    },
    {
      id: 'user-management',
      name: 'User Management',
      icon: Shield,
      tab: 'admin',
      description: 'Admin-only user and permission management',
      // Collapsed from seven sibling entries to four (Sprint 2 batch 2):
      //   - Admin Dashboard (entry point)
      //   - Users          (the people list)
      //   - Roles & Permissions (Permission Center; absorbs User
      //                         Approvals + Audit Log + the old RBAC stub)
      //   - Communications (Email Templates + Pax Banners + future SMS)
      //
      // Old standalone routes remain in PaxApp/PaxAppRoutes so deep links
      // keep working, but the sidebar no longer surfaces the duplicates.
      items: [
        { id: 'admin-dashboard', name: 'Admin Dashboard', icon: LayoutDashboard, path: '/admin' },
        { id: 'user-management', name: 'Users', icon: Users, path: '/admin/users' },
        { id: 'approvals', name: 'Approvals', icon: ListChecks, path: '/admin/approvals' },
        { id: 'permissions', name: 'Roles & Permissions', icon: Shield, path: '/admin/permissions' },
        { id: 'communications', name: 'Communications', icon: Mail, path: '/admin/email-templates' },
      ]
    },
    {
      id: 'loyalty-admin',
      name: 'Loyalty Administration',
      icon: Award,
      tab: 'admin',
      description: 'Loyalty program configuration',
      items: [
        { id: 'loyalty-programs', name: 'Loyalty Programs', icon: Award, path: '/admin/loyalty-programs' },
        { id: 'earning-rules', name: 'Earning Rules', icon: Calculator, path: '/admin/earning-rules' },
        { id: 'earning-charts', name: 'Earning Charts', icon: BarChart3, path: '/admin/earning-charts' },
        { id: 'calculation-tester', name: 'Calculation Tester', icon: Calculator, path: '/admin/calculation-tester' },
        { id: 'airline-classes', name: 'Airline Class Mapping', icon: Plane, path: '/admin/airline-classes' },
      ]
    },
    {
      id: 'system-admin',
      name: 'System Admin',
      icon: ServerCog,
      tab: 'admin',
      description: 'System administration tools',
      items: [
        { id: 'system-health', name: 'System Health', icon: Activity, path: '/admin/system-health' },
        { id: 'sla-dashboard', name: 'SLA Dashboard', icon: TrendingUp, path: '/admin/sla-dashboard' },
        { id: 'pax-banners', name: 'Pax Banners', icon: AlertTriangle, path: '/admin/pax-banners' },
        { id: 'pax-content', name: 'Pax Content (CMS)', icon: FileText, path: '/admin/pax-content', isNew: true },
        { id: 'pax-invite', name: 'Pax Invitations', icon: Mail, path: '/admin/pax-invite' },
        { id: 'pbx-routing', name: 'PBX Routing', icon: Phone, path: '/admin/pbx-routing' },
        { id: 'login-cms', name: 'Login Screen CMS', icon: Settings, path: '/admin/login-cms' },
        { id: 'system-manual', name: 'System Manual', icon: Book, path: '/admin/system-manual' },
        { id: 'managed-lists', name: 'Managed Lists', icon: ListChecks, path: '/admin/managed-lists', isNew: true },
      ]
    },
    {
      id: 'ingestion-admin',
      name: 'Ingestion & Monitoring',
      icon: Radio,
      tab: 'admin',
      description: 'Queue pollers, supplier invoice ingestion, platform events, reports',
      items: [
        { id: 'booking-queue', name: 'Booking Queue Monitor', icon: Activity, path: '/admin/booking-queue', isNew: true },
        { id: 'supplier-pollers', name: 'Supplier Pollers', icon: Mail, path: '/admin/supplier-pollers', isNew: true },
        { id: 'automations', name: 'Automation Control Center', icon: Zap, path: '/admin/automations', isNew: true },
        { id: 'delivery', name: 'Delivery and decisions', icon: ListChecks, path: '/admin/delivery', isNew: true },
        { id: 'rtw-fare-rules', name: 'RTW filed fare rules', icon: Shield, path: '/admin/rtw-fare-rules', isNew: true },
        { id: 'platform-events', name: 'Platform Events', icon: Radio, path: '/admin/platform-events', isNew: true },
        { id: 'gds-remark-review', name: 'GDS Remark Review', icon: Shield, path: '/admin/gds-remark-review', isNew: true },
        { id: 'fd-import', name: 'Freshdesk Import Monitor', icon: Inbox, path: '/admin/fd-import' },
      ]
    },
    {
      id: 'public-portal',
      name: 'Public Portal (Pax)',
      icon: Globe,
      tab: 'admin',
      description: 'Pax-facing tools — every link below mirrors what a magic-link customer sees on pax.propellertravel.com',
      items: [
        { id: 'pax-availability', name: 'Live Availability', icon: Terminal, path: '/propeller/availability', isNew: true },
        { id: 'pax-fare-quote', name: 'Fare Quote', icon: Receipt, path: '/propeller/fare-quote', isNew: true },
        { id: 'pax-price-explorer', name: 'Price Explorer', icon: TrendingDown, path: '/propeller/price-explorer', isNew: true },
        { id: 'flight-search', name: 'Portal Flight Search', icon: SearchIcon, path: '/propeller/search' },
        { id: 'alerts', name: 'Availability Alerts', icon: AlertTriangle, path: '/propeller/alerts' },
        { id: 'flifo', name: 'Flight Information', icon: Activity, path: '/propeller/flifo' },
        { id: 'mct', name: 'MCT Checker', icon: Clock, path: '/propeller/mct' },
        { id: 'pax-profile', name: 'Pax Profile', icon: User, path: '/propeller/profile' },
        { id: 'award-search', name: 'Award Search (staff)', icon: Award, path: '/propeller/award-search' },
        { id: 'seat-maps-staff', name: 'Seat Maps', icon: Grid, path: '/propeller/seat-maps' },
        { id: 'propeller-settings', name: 'Portal Settings', icon: Settings, path: '/propeller/settings', isNew: true },
      ]
    },
  ], [isPresentationMode]);

  // Dynamic route permission check
  useEffect(() => {
    if (!user?.id) {
      setPermissionState({ userId: null, status: 'ready', paths: new Set() });
      return;
    }

    const userId = user.id;
    let active = true;
    setPermissionState({ userId, status: 'pending', paths: new Set() });

    async function evaluatePermissions() {
      try {
        const { checkRouteAccess } = await import('../../services/unifiedPermissionService');
        const allowed = new Set<string>();

        // Collect all distinct paths from baseNavigationSections
        const pathsToCheck = Array.from(
          new Set(
            [
              ...baseNavigationSections.flatMap(section => section.items.map(item => (
                getNavigationPermissionPath(item.path)
              ))),
              ...ADDITIONAL_SEARCH_PERMISSION_PATHS,
            ]
          )
        );

        // Run checkRouteAccess for each path
        const results = await Promise.all(
          pathsToCheck.map(async path => {
            try {
              const res = await checkRouteAccess(userId, path);
              return { path, hasAccess: res.hasAccess, failed: false };
            } catch (err) {
              console.error(`Permission check failed for path ${path}:`, err);
              return { path, hasAccess: false, failed: true };
            }
          })
        );

        if (!active) return;

        if (results.some((result) => result.failed)) {
          setPermissionState({ userId, status: 'error', paths: new Set() });
          return;
        }

        results.forEach(res => {
          if (res.hasAccess) {
            allowed.add(res.path);
          }
        });

        // Always allow homepage (/) by default
        allowed.add('/');

        setPermissionState({ userId, status: 'ready', paths: allowed });
      } catch (err) {
        console.error('Error evaluating route permissions:', err);
        if (active) {
          setPermissionState({ userId, status: 'error', paths: new Set() });
        }
      }
    }

    evaluatePermissions();

    return () => {
      active = false;
    };
  }, [user?.id, baseNavigationSections]);

  useEffect(() => {
    // Empty effective permissions during evaluation/error deliberately clear
    // persisted recents instead of leaving stale destinations visible.
    filterRecentPages(allowedPaths);
  }, [allowedPaths, filterRecentPages]);

  // Derived, never hardcoded: a connection that syncs clears this without a code change.
  const { health: bankFeedHealth } = useBankFeedHealth();
  // Memoised because navigationSections depends on it by identity; a fresh object each
  // render would rebuild the whole nav on every render.
  const bankFeedBadge = useMemo(() => bankFeedNavBadge(bankFeedHealth), [bankFeedHealth]);

  // Menu items that start hidden (Routines, QC Checklists, Doc Acknowledgements) show only while an administrator's
  // "show in the menu" setting says so. Until the setting has been read, and if it cannot be read, they stay hidden.
  const [shownGatedMenuKeys, setShownGatedMenuKeys] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (!user?.id) {
      setShownGatedMenuKeys(new Set());
      return;
    }
    let active = true;
    const load = () => {
      fetchMenuItemSettings()
        .then((rows) => { if (active) setShownGatedMenuKeys(shownMenuKeys(rows)); })
        .catch(() => { if (active) setShownGatedMenuKeys(new Set()); });
    };
    load();
    window.addEventListener(MENU_SETTINGS_CHANGED_EVENT, load);
    return () => {
      active = false;
      window.removeEventListener(MENU_SETTINGS_CHANGED_EVENT, load);
    };
  }, [user?.id]);

  const navigationSections = useMemo(() => {
    if (permissionsLoading) return [];

    return baseNavigationSections
      .map(section => ({
        ...section,
        items: section.items
          .filter(item => allowedPaths.has(getNavigationPermissionPath(item.path)))
          .filter(item => !(GATED_MENU_KEYS as readonly string[]).includes(item.id) || shownGatedMenuKeys.has(item.id))
          .map(item =>
            bankFeedBadge && BANK_FEED_NAV_IDS.has(item.id)
              ? { ...item, unavailable: bankFeedBadge.reason, unavailableLabel: bankFeedBadge.label }
              : item,
          )
          .map(item => (item.id === 'queue-center' ? { ...item, badge: queueBadge.count } : item)),
      }))
      .filter(section => section.items.length > 0);
  }, [baseNavigationSections, allowedPaths, permissionsLoading, bankFeedBadge, queueBadge.count, shownGatedMenuKeys]);

  // Determine which tabs have at least one visible section
  const allowedTabIds = useMemo(() => {
    const tabsSet = new Set<string>();
    navigationSections.forEach(section => {
      tabsSet.add(section.tab);
    });
    return Array.from(tabsSet) as ('operations' | 'crm' | 'finance' | 'intelligence' | 'admin')[];
  }, [navigationSections]);

  const domainShortcutTargets = useMemo(() => {
    const targets = new Map<NavSection['tab'], string>();
    for (const domain of allowedTabIds) {
      const preferred = DOMAIN_LANDING_PATHS[domain];
      const fallbacks = navigationSections
        .filter((section) => section.tab === domain)
        .flatMap((section) => section.items.map((item) => item.path));
      const target = getAllowedShortcutTarget(allowedPaths, preferred, fallbacks);
      if (target) targets.set(domain, target);
    }
    return targets;
  }, [allowedPaths, allowedTabIds, navigationSections]);

  // Get sections for active tab
  const activeSections = useMemo(
    () => navigationSections.filter(section => section.tab === activeTab),
    [activeTab, navigationSections],
  );

  // Flatten all items for favorites component
  const allNavItems: NavItem[] = navigationSections.flatMap(section => section.items);

  // Track page visits for recents & Set Page Title
  useEffect(() => {
    const currentPath = location.pathname;

    // Find name for current path
    let pageName = 'Propeller Travel Platform';
    for (const section of navigationSections) {
      const item = section.items.find(i => {
        // Handle dynamic routes like /customers/:id
        if (i.path.includes(':')) {
          const basePath = i.path.split(':')[0];
          return currentPath.startsWith(basePath);
        }
        return i.path === currentPath;
      });
      if (item) {
        pageName = `${item.name} - Propeller Travel Platform`;
        break;
      }
    }

    // Special handling for commonly accessed dynamic pages if not found in nav
    if (pageName === 'Propeller Travel Platform') {
      if (currentPath.includes('/customers/')) pageName = 'Customer - Propeller Travel Platform';
      else if (currentPath.includes('/trips/')) pageName = 'Trip - Propeller Travel Platform';
      else if (currentPath.includes('/invoices/')) pageName = 'Invoice - Propeller Travel Platform';
      else if (currentPath.includes('/pnr/record/') || /^\/pnr\/[a-z0-9]{2}-[a-z0-9]+/i.test(currentPath)) pageName = 'PNR Record - Propeller Travel Platform';
    }

    document.title = pageName;

    const permissionPath = getNavigationAuthorizationPath(currentPath);
    if (user?.id && allowedPaths.has(permissionPath)) {
      addRecentPage({
        id: currentPath,
        name: pageName.split(' - ')[0],
        path: currentPath,
        permissionPath,
        timestamp: Date.now(),
      }, user.id);
    }
  }, [location.pathname, navigationSections, addRecentPage, allowedPaths, user?.id]);

  // Keyboard shortcuts
  useEffect(() => {
    const domainKeys: Record<string, 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin'> = {
      '1': 'operations',
      '2': 'crm',
      '3': 'finance',
      '4': 'intelligence',
      '5': 'admin',
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K and Escape for the palettes are owned by useCommandContext (one active command surface per keypress).
      // The slash shortcut opens the same comprehensive palette as Cmd/Ctrl+K.
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        const isEditing = target?.isContentEditable
          || target?.tagName === 'INPUT'
          || target?.tagName === 'TEXTAREA'
          || target?.tagName === 'SELECT';
        if (!isEditing) {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
      // Domain switching: Alt+1-5
      if (e.altKey && domainKeys[e.key]) {
        const domain = domainKeys[e.key];
        const target = domainShortcutTargets.get(domain);
        if (!target) return;
        e.preventDefault();
        setActiveTab(domain);
        navigate(target);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [domainShortcutTargets, navigate, setActiveTab]);

  // Keep the sidebar mode aligned with the current route. The menu decides (the section holding the matching item),
  // and the mode the person is in is kept when the route is listed there too. The URL-word table below is only a
  // fallback for routes the menu does not list; an unknown route keeps the current mode (it used to jump to
  // Operations, e.g. for /suppliers/rates, /expenses, /compliance or /knowledge-base).
  const routeAlignmentRef = useRef<{ path: string; withMenu: boolean } | null>(null);
  useEffect(() => {
    const sectionsReady = navigationSections.length > 0;
    // Re-align only when the path changes, plus once when the menu first becomes available for this path. A
    // permission re-check, a badge update or a query-string change never undoes a mode picked by hand on this page.
    const aligned = routeAlignmentRef.current;
    if (aligned && aligned.path === location.pathname && (aligned.withMenu || !sectionsReady)) return;
    routeAlignmentRef.current = { path: location.pathname, withMenu: sectionsReady };
    const firstSegment = location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
    const tabBySegment: Record<string, 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin'> = {
      crm: 'crm',
      customers: 'crm',
      companies: 'crm',
      travelers: 'crm',
      leads: 'crm',
      communications: 'crm',
      ucc: 'crm',
      'communication-logs': 'crm',
      'communication-hub': 'crm',
      documents: 'crm',
      'vip-support': 'crm',
      tasks: 'operations',
      'action-plans': 'operations',
      routines: 'operations',
      'qc-checklists': 'operations',
      finance: 'finance',
      invoices: 'finance',
      payments: 'finance',
      banking: 'finance',
      commissions: 'finance',
      partners: 'finance',
      reports: 'intelligence',
      analytics: 'intelligence',
      revenue: 'intelligence',
      alerts: 'intelligence',
      risk: 'intelligence',
      ai: 'intelligence',
      'ai-assistant': 'intelligence',
      admin: 'admin',
      settings: 'admin',
      integrations: 'admin',
      automations: 'admin',
    };

    const currentTab = useNavigationStore.getState().activeTab;
    const nextTab = resolveRouteTab(
      navigationSections,
      location.pathname,
      location.search,
      currentTab,
      tabBySegment[firstSegment] ?? (firstSegment === 'dashboard' ? 'operations' : null),
    );
    if (nextTab !== currentTab) useNavigationStore.setState({ activeTab: nextTab });
  }, [location.pathname, location.search, navigationSections]);

  // Keep the current page's section open after navigating (exact item first, then its parent item, so detail
  // pages such as /crm/customers/123 keep "Customers" open). Other open or closed sections are left as they are.
  useEffect(() => {
    const sectionId = findRouteSectionId(activeSections, location.pathname, location.search);
    if (sectionId) expandSection(sectionId);
  }, [location.pathname, location.search, activeTab, activeSections, expandSection, navigationPreferencesBound, navigationPreferencesStatus]);

  const densityClass = {
    compact: 'nav-density-compact',
    normal: 'nav-density-normal',
    comfortable: 'nav-density-comfortable',
  }[density];

  const sidebarInteractive = isDesktopViewport ? !isSidebarCollapsed : sidebarOpen;
  const mobileNavigationModalOpen = !isDesktopViewport && sidebarOpen;

  useEffect(() => {
    const sidebar = sidebarNavRef.current;
    if (!sidebar) return;
    if (sidebarInteractive) sidebar.removeAttribute('inert');
    else sidebar.setAttribute('inert', '');
  }, [sidebarInteractive]);

  const closeMobileSidebar = useCallback((restoreFocus = true) => {
    setSidebarOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => mobileMenuButtonRef.current?.focus(), 0);
    }
  }, []);

  const handleDesktopSidebarToggle = useCallback(() => {
    const willExpand = isSidebarCollapsed;
    toggleSidebar();
    window.setTimeout(() => {
      (willExpand ? desktopCollapseButtonRef : desktopExpandButtonRef).current?.focus();
    }, 0);
  }, [isSidebarCollapsed, toggleSidebar]);

  useEffect(() => {
    sidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  useEffect(() => {
    const routeChanged = previousLocationPathRef.current !== location.pathname;
    previousLocationPathRef.current = location.pathname;
    if (routeChanged && sidebarOpenRef.current) {
      setSidebarOpen(false);
      window.setTimeout(() => mainContentRef.current?.focus(), 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isDesktopViewport || !sidebarOpen) return;
    const focusTimer = window.setTimeout(() => mobileCloseButtonRef.current?.focus(), 0);
    const handleDrawerKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobileSidebar(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const drawer = document.getElementById('main-navigation');
      if (!drawer) return;
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleDrawerKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleDrawerKeyDown, true);
    };
  }, [closeMobileSidebar, isDesktopViewport, sidebarOpen]);

  return (
    <div className="min-h-screen bg-gray-50" data-overlay-open={overlayOpen ? 'true' : undefined}>
      {/* PT-AUDIT-0228: first focusable element of the staff shell */}
      <SkipToContentLink />
      {/* Staging Environment Banner */}
      <StagingBanner />
      {/* Mobile Header */}
      <div data-app-top-chrome="" className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <img src={PropellerLogo} alt="Propeller Travel" className="h-8 w-auto" />
        </div>
        <div className="flex items-center space-x-3">
          <PresentationModeToggle />
          {user?.id && <NotificationBell />}
          <button
            onClick={() => {
              setSidebarOpen(false);
              setSearchOpen(true);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            aria-label="Search"
          >
            <SearchIcon className="w-5 h-5" />
          </button>
          <button
            ref={mobileMenuButtonRef}
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            aria-label="Open navigation menu"
            aria-controls="main-navigation"
            aria-expanded={sidebarOpen}
            aria-haspopup="dialog"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Desktop Search Button */}
      <div className="hidden lg:flex fixed top-4 right-4 z-30 items-center space-x-3">
        <PresentationModeToggle />
        {user?.id && (
          <NotificationBell className="bg-white border border-gray-300 shadow-sm" />
        )}
        <button
          onClick={() => setSearchOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:shadow-md transition-shadow text-sm text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          title="Search (Cmd/Ctrl+K or /)"
          aria-label="Search pages, actions, and records"
        >
          <SearchIcon className="w-4 h-4" />
          <span>Search</span>
          <kbd className="hidden xl:inline-flex items-center px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
            Ctrl/⌘ K
          </kbd>
        </button>
      </div>

      {/* Desktop expand button */}
      {isSidebarCollapsed && (
        <button
          ref={desktopExpandButtonRef}
          onClick={handleDesktopSidebarToggle}
          className="hidden lg:flex fixed top-4 left-4 z-40 p-2.5 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md text-gray-600 hover:text-indigo-600 transition-all"
          title="Expand sidebar"
          aria-label="Expand sidebar"
          aria-controls="main-navigation"
          aria-expanded="false"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar */}
      <MobileNavigationModalBoundary
        active={mobileNavigationModalOpen}
        initialFocusRef={mobileCloseButtonRef}
        onRequestClose={() => closeMobileSidebar(true)}
      >
        <nav
          ref={sidebarNavRef}
          id="main-navigation"
          className={`fixed inset-y-0 left-0 z-40 w-64 overflow-hidden bg-white border-r border-gray-200 transform transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } ${isSidebarCollapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'} ${densityClass}`}
          aria-label="Main navigation"
          aria-hidden={!sidebarInteractive}
        >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="min-w-0">
              <h2 id="mobile-navigation-title" className="sr-only">Main navigation</h2>
              <img src={PropellerLogo} alt="Propeller Travel" className="h-7 w-auto" />
              <p className="mt-1 truncate text-[10px] font-medium uppercase tracking-wide text-gray-500">Management Platform</p>
            </div>
            <button
              ref={desktopCollapseButtonRef}
              onClick={handleDesktopSidebarToggle}
              className="hidden rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:block"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              aria-controls="main-navigation"
              aria-expanded="true"
            >
              <ChevronsLeft className="w-5 h-5" />
            </button>
            <button
              ref={mobileCloseButtonRef}
              onClick={() => closeMobileSidebar(true)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:hidden"
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabbed Navigation */}
          {navigationPreferencesBound && (
            <TabbedNavigation allowedTabIds={allowedTabIds} tabTargets={domainShortcutTargets} />
          )}

          {/* Navigation Search */}
          <NavigationSearch onOpenSearch={() => {
            setSidebarOpen(false);
            setSearchOpen(true);
          }} />

          {/* Scrollable Navigation */}
          <nav className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-3 py-2" aria-label="Platform pages">
            {!navigationPreferencesBound ? (
              <div className="flex items-center justify-center py-8" aria-live="polite">
                <LoadingSpinner size="sm" />
                <span className="sr-only">Loading navigation preferences</span>
              </div>
            ) : (
            <>
            {/* Customizable shortcuts are part of the scrollable menu, not fixed chrome. */}
            <QuickActionsBar allNavItems={allNavItems} />

            {/* Favorites */}
            <NavigationFavorites allNavItems={allNavItems} />

            {/* Most Used */}
            <NavigationMostUsed allNavItems={allNavItems} limit={5} />

            {/* Recent Pages */}
            <NavigationRecents currentUserId={currentUserId} allowedPaths={allowedPaths} />

            {/* Divider */}
            {(allNavItems.length > 0) && <div className="nav-divider" />}

            {/* Navigation Sections */}
            <div className="space-y-2">
              {/* Expand/Collapse All */}
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Modules</span>
                <div className="flex items-center gap-2">
                <button
                  onClick={collapseAll}
                  className="rounded px-1 py-0.5 text-[11px] text-gray-500 hover:bg-slate-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  Collapse
                </button>
                <button
                  onClick={() => expandAll(activeSections.map((section) => section.id))}
                  className="rounded px-1 py-0.5 text-[11px] text-gray-500 hover:bg-slate-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  Expand
                </button>
                </div>
              </div>

              {/* Render sections for active tab */}
              {permissionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                activeSections.map((section) => (
                  <NavigationSection
                    key={section.id}
                    id={section.id}
                    name={section.name}
                    icon={section.icon}
                    items={section.items}
                    description={section.description}
                  />
                ))
              )}
            </div>

            {/* Suggested Next Steps */}
            <NavigationSuggestedNext />

            {/* Keyboard Shortcuts */}
            <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">
              <NavigationShortcuts />
              <NavigationSettings />
              <NavigationHelp />
            </div>
            </>
            )}
          </nav>

          {/* Footer */}
          <div className="border-t border-gray-200 px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200">
                <span className="text-sm font-medium text-gray-700">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-gray-900">{user?.email}</p>
                <p className="truncate text-[10px] text-gray-500">
                  {userRole ? formatRoleLabel(userRole) : 'Loading...'}
                </p>
              </div>
            <button
              onClick={signOut}
              className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
            </div>
            <div className="mt-1 px-1 text-[10px]">
              <VersionDisplay isAdmin={isAdmin} />
            </div>
          </div>
        </div>
        </nav>
      </MobileNavigationModalBoundary>

      {/* Main Content */}
      <div className={`transition-all duration-300 pt-16 lg:pt-0 ${isSidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64'}`}>
        {isSuperAdminUser && <WhatsAppSystemStatusBar compactOnMobile={location.pathname === '/ops/dispatch'} />}
        {/* Fixed Page Help and Quick Actions controls occupy the bottom-right.
            Keep enough scroll clearance for page-level action buttons to move
            fully above their visible and pointer hit areas at every breakpoint.
            From 1024 px the two buttons sit in a slim column on the right edge. The
            CRM pages (dense tables) keep a matching right gutter (lg:pr-20) so the
            buttons never cover a column or a card; other pages keep their full width. */}
        <main
          ref={mainContentRef}
          id={MAIN_CONTENT_ID}
          className={`p-4 pb-32 sm:p-6 sm:pb-32${keepsFloatingButtonGutter ? ' lg:pr-20' : ''}`}
          role="main"
          aria-label="Main content"
          tabIndex={-1}
        >
          {operatorDesignRoute ? (
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <Breadcrumbs />
              <OperatorDesignKey />
            </div>
          ) : <Breadcrumbs />}
          {children ? children : <Outlet />}
        </main>
      </div>

      {/* Page Help Button */}
      <PageHelpButton className={location.pathname === '/ops/dispatch' ? 'max-sm:hidden' : ''} hidden={overlayOpen} />

      {/* Master Command Palette */}
      <MasterSearchPalette
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpen={() => setSearchOpen(true)}
        navigationSections={navigationSections}
        allowedPaths={allowedPaths}
      />

      {/* "View as customer…" staff impersonation palette. Cmd/Ctrl+Shift+K
          opens it globally; only staff roles see it surfaced anywhere. */}
      {(userRole === 'admin' || userRole === 'super_admin' || userRole === 'manager') && (
        <ViewAsCustomerPalette open={viewAsOpen} onClose={() => setViewAsOpen(false)} />
      )}

      {/* Quick Actions Menu */}
      <div className={location.pathname === '/ops/dispatch' ? 'hidden sm:contents' : 'contents'}>
        <QuickActionsMenu
          hidden={overlayOpen}
          allowedPaths={allowedPaths}
          onNavigate={(path) => {
            navigate(path);
          }}
          onAskAI={() => {
            navigate('/ai-assistant');
            setSearchOpen(false);
          }}
        />
      </div>

      {user?.id && (
        <NotificationPanel
          userId={user.id}
          onNotificationClick={(notification) => {
            const action = notification.actions?.find((item) => item.primary)?.action
              || notification.actions?.[0]?.action;
            if (action?.startsWith('/')) {
              navigate(action);
            }
          }}
        />
      )}

      {/* Global Unified AI Co-Pilot & Sabre Emergency Launcher (on non-/ops/today screens) */}
      {location.pathname !== '/ops/today' && location.pathname !== '/ops' && (
        <div className="contents" data-floating-launcher="copilot" data-launcher-hidden={overlayOpen ? 'true' : undefined}>
          <DkManagementAssistantDrawer floatingLauncher pageContext={location.pathname} />
        </div>
      )}

      {/* Onboarding Tour */}
      <NavigationOnboarding />
    </div>
  );
}
